/**
 * OCT-3366: per-entity migration banner.
 *
 * Renders on entities 2..N of multi-entity workspaces that previously
 * shared resources at the workspace scope (reminders, tax rates, chart of
 * accounts, integration connections). The server-side
 * GET /api/entities/:entityId/migration-status decides when to render -
 * the banner only fires when every counted operational resource is zero
 * (the post-migration fingerprint).
 *
 * Hidden on:
 *   - the workspace default entity (the backfill stamped existing rows
 *     onto the default, so it always has data)
 *   - the All Entities rollup (no per-entity context)
 *   - dismissed (user, entity) pairs (localStorage key per pair)
 *
 * HITL: the exact banner copy, icon, and link styling are pending design
 * review (per the ticket). The component carries `// OCT-3366:HITL`
 * markers at the spots that need polish - keep the structure, swap the
 * presentation.
 */
import { Button } from "@/components/ui/button";
import { useActiveEntity } from "@/lib/entity-context";
import { getQueryFn } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { Info, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

interface MigrationStatus {
  needsBanner: boolean;
  counts: {
    integrationConnections: number;
    taxRates: number;
    chartOfAccounts: number;
    reminderRules: number;
    // OCT-10688: message templates became entity-bound alongside reminder
    // rules; a fifth zero joins the "still blank" AND-condition server-side.
    automationMessageTemplates: number;
  };
  entityId: string;
  isDefault: boolean;
}

const DISMISS_STORAGE_PREFIX = "octa-entity-migration-banner-dismissed:";

function getDismissKey(userId: string | null, entityId: string): string {
  return `${DISMISS_STORAGE_PREFIX}${userId ?? "anon"}:${entityId}`;
}

function readDismissed(userId: string | null, entityId: string): boolean {
  try {
    return localStorage.getItem(getDismissKey(userId, entityId)) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(userId: string | null, entityId: string): void {
  try {
    localStorage.setItem(getDismissKey(userId, entityId), "1");
  } catch {} /* dismissal is best-effort; never block render on storage */
}

export interface EntityMigrationBannerProps {
  /** Caller-supplied user id so the dismissal key includes the actor. */
  userId: string | null;
}

export function EntityMigrationBanner({ userId }: EntityMigrationBannerProps) {
  const { t } = useTranslation("common");
  const [, setLocation] = useLocation();
  const { activeEntity, activeEntityId, isAllEntities } = useActiveEntity();
  const [dismissed, setDismissed] = useState<boolean>(() =>
    activeEntityId && !isAllEntities
      ? readDismissed(userId, activeEntityId)
      : true
  );

  // OCT-3366: when the operator switches entity, re-evaluate dismissal
  // for the new pair; an entity dismissed before should stay dismissed,
  // a fresh entity should show its banner.
  useEffect(() => {
    if (!activeEntityId || isAllEntities) {
      setDismissed(true);
      return;
    }
    setDismissed(readDismissed(userId, activeEntityId));
  }, [userId, activeEntityId, isAllEntities]);

  const { data: status } = useQuery<MigrationStatus>({
    queryKey: ["/api/entities", activeEntityId, "migration-status"],
    queryFn: getQueryFn<MigrationStatus>({ on401: "returnNull" }),
    enabled: !!activeEntityId && !isAllEntities && !dismissed,
  });

  const handleDismiss = useCallback(() => {
    if (!activeEntityId) return;
    writeDismissed(userId, activeEntityId);
    setDismissed(true);
  }, [userId, activeEntityId]);

  if (isAllEntities) return null;
  if (!activeEntityId || !activeEntity) return null;
  if (dismissed) return null;
  if (!status?.needsBanner) return null;

  return (
    <div
      className="flex items-start gap-3 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs sm:text-sm"
      data-testid="banner-entity-migration"
      role="status"
    >
      <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-amber-900 dark:text-amber-100" data-testid="text-entity-migration-title">
          {
            /* OCT-3366:HITL — copy review. The headline names the entity so the
             operator immediately sees which one is blank. */
          }
          {t("entityMigrationBanner.title")} - {activeEntity.name}
        </p>
        <p className="text-amber-800 dark:text-amber-200 mt-0.5" data-testid="text-entity-migration-message">
          {t("entityMigrationBanner.message")}
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          {
            /* OCT-3366:HITL — link copy + ordering. The four links match the
             four counts the server reports; a future polish pass may hide
             individual links once a resource is configured. */
          }
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLocation("/settings?section=integrations&sub=sync")}
            data-testid="link-entity-migration-connect"
          >
            {t("entityMigrationBanner.connect")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLocation("/ar?tab=reminders")}
            data-testid="link-entity-migration-reminders"
          >
            {t("entityMigrationBanner.reminders")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLocation("/settings?section=tax-rates")}
            data-testid="link-entity-migration-tax"
          >
            {t("entityMigrationBanner.taxRates")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLocation("/settings?section=chart-of-accounts")}
            data-testid="link-entity-migration-coa"
          >
            {t("entityMigrationBanner.chartOfAccounts")}
          </Button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="shrink-0 p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors"
        aria-label={t("entityMigrationBanner.dismiss")}
        data-testid="button-entity-migration-dismiss"
      >
        <X className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
      </button>
    </div>
  );
}
